import React from "react";
import { Row, ColumnDef } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";

export type AccordionGroupingState = {
  groupByColumn?: string;
  expandedGroups: Set<string>;
};

export type AccordionGroupingActions = {
  setGroupByColumn: (columnId?: string) => void;
  toggleGroup: (groupKey: string) => void;
  expandAllGroups: () => void;
  collapseAllGroups: () => void;
};

export type AccordionGroup<TData> = {
  key: string;
  value: any;
  rows: Row<TData>[];
  count: number;
};

export function useAccordionGrouping<TData>(initialGroupByColumn?: string): {
  state: AccordionGroupingState;
  actions: AccordionGroupingActions;
} {
  const [state, setState] = React.useState<AccordionGroupingState>({
    groupByColumn: initialGroupByColumn,
    expandedGroups: new Set(),
  });

  const actions = React.useMemo<AccordionGroupingActions>(
    () => ({
      setGroupByColumn: (columnId?: string) => {
        setState((prev) => ({
          ...prev,
          groupByColumn: columnId,
          expandedGroups: new Set(), // Reset expanded state when changing grouping
        }));
      },
      toggleGroup: (groupKey: string) => {
        setState((prev) => {
          const newExpanded = new Set(prev.expandedGroups);
          if (newExpanded.has(groupKey)) {
            newExpanded.delete(groupKey);
          } else {
            newExpanded.add(groupKey);
          }
          return {
            ...prev,
            expandedGroups: newExpanded,
          };
        });
      },
      expandAllGroups: () => {
        setState((prev) => ({
          ...prev,
          expandedGroups: new Set(), // Will be populated when groups are rendered
        }));
      },
      collapseAllGroups: () => {
        setState((prev) => ({
          ...prev,
          expandedGroups: new Set(),
        }));
      },
    }),
    [],
  );

  return { state, actions };
}

export function groupRowsByColumn<TData>(
  rows: Row<TData>[],
  columnId: string,
): AccordionGroup<TData>[] {
  const groups = new Map<string, AccordionGroup<TData>>();

  rows.forEach((row) => {
    const column = row
      .getAllCells()
      .find((cell) => cell.column.id === columnId);
    const value = column?.getValue();
    const key = String(value ?? "");

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        value,
        rows: [],
        count: 0,
      });
    }

    const group = groups.get(key)!;
    group.rows.push(row);
    group.count++;
  });

  // Sort groups by key
  return Array.from(groups.values()).sort((a, b) => a.key.localeCompare(b.key));
}

export interface AccordionGroupHeaderProps<TData> {
  group: AccordionGroup<TData>;
  isExpanded: boolean;
  onToggle: () => void;
  columnCount: number;
  groupByColumn: string;
}

export function AccordionGroupHeader<TData>({
  group,
  isExpanded,
  onToggle,
  columnCount,
  groupByColumn,
}: AccordionGroupHeaderProps<TData>) {
  return (
    <tr className="border-b">
      <td colSpan={columnCount} className="p-0">
        <div className="flex">
          <button
            className={cn(
              "flex flex-1 items-center h-10 px-2 font-medium transition-all hover:underline",
              "[&[data-state=open]>svg]:rotate-180",
            )}
            data-state={isExpanded ? "open" : "closed"}
            onClick={onToggle}
          >
            <div className="flex items-center gap-2">
              <span className="capitalize">{String(group.value)}</span>
              <span className="text-sm text-muted-foreground font-normal">
                ({group.count})
              </span>
              <ChevronDownIcon className="h-4 w-4 shrink-0 mr-2 text-muted-foreground transition-transform duration-200" />
            </div>
          </button>
        </div>
      </td>
    </tr>
  );
}

export interface AccordionTableRowsProps<TData> {
  groups: AccordionGroup<TData>[];
  expandedGroups: Set<string>;
  onToggleGroup: (groupKey: string) => void;
  columns: ColumnDef<TData, any>[];
  groupByColumn: string;
  renderRow: (row: Row<TData>) => React.ReactNode;
  columnSizeVars?: { [key: string]: string };
}

export function AccordionTableRows<TData>({
  groups,
  expandedGroups,
  onToggleGroup,
  columns,
  groupByColumn,
  renderRow,
  columnSizeVars,
}: AccordionTableRowsProps<TData>) {
  return (
    <>
      {groups.map((group) => {
        const isExpanded = expandedGroups.has(group.key);

        return (
          <React.Fragment key={group.key}>
            <AccordionGroupHeader
              group={group}
              isExpanded={isExpanded}
              onToggle={() => onToggleGroup(group.key)}
              columnCount={columns.length}
              groupByColumn={groupByColumn}
            />
            {isExpanded && (
              <tr>
                <td colSpan={columns.length} className="p-0">
                  <div className="overflow-hidden transition-all duration-200 ease-in-out">
                    <div className="pb-0">
                      <table
                        className="w-full"
                        style={{
                          tableLayout: "fixed",
                          borderCollapse: "separate",
                          borderSpacing: 0,
                          ...columnSizeVars,
                        }}
                      >
                        <colgroup>
                          {columns.map((column) => (
                            <col
                              key={column.id || (column as any).accessorKey}
                              style={{
                                width: `calc(var(--col-${column.id || (column as any).accessorKey}-size))`,
                              }}
                            />
                          ))}
                        </colgroup>
                        <tbody>
                          {group.rows.map((row) => (
                            <React.Fragment key={row.id}>
                              {renderRow(row)}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

export interface AccordionControlsProps {
  columns: ColumnDef<any, any>[];
  groupByColumn?: string;
  onGroupByColumnChange: (columnId?: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  hasGroups: boolean;
}

export function AccordionControls({
  columns,
  groupByColumn,
  onGroupByColumnChange,
  onExpandAll,
  onCollapseAll,
  hasGroups,
}: AccordionControlsProps) {
  const groupableColumns = columns.filter(
    (col) => col.id && col.id !== "select" && col.id !== "actions",
  );

  return (
    <div className="flex items-center justify-between p-3 border-b bg-muted/30">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-foreground">Group by:</span>
        <select
          value={groupByColumn || ""}
          onChange={(e) => onGroupByColumnChange(e.target.value || undefined)}
          className={cn(
            "flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm",
            "ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium",
            "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            "min-w-[180px]",
          )}
        >
          <option value="">No grouping</option>
          {groupableColumns.map((column) => (
            <option key={column.id} value={column.id}>
              {typeof column.header === "string" ? column.header : column.id}
            </option>
          ))}
        </select>
      </div>

      {groupByColumn && hasGroups && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onExpandAll}
            className="h-8 px-3 text-xs font-medium"
          >
            Expand All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onCollapseAll}
            className="h-8 px-3 text-xs font-medium"
          >
            Collapse All
          </Button>
        </div>
      )}
    </div>
  );
}
